import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';

const PaymentGuard = ({ children }) => {
  const { loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);

  // Track the user_id and owner_id being watched for realtime
  const watchedUserIdRef = useRef(null);
  const realtimeChannelRef = useRef(null);

  // Re-show banner on every page navigation (no DB query — uses memory)
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  // On mount: resolve which userId to watch, then subscribe (status query happens inside SUBSCRIBED)
  useEffect(() => {
    if (loading) return;

    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Check if user is a team_member
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('subscription_tier')
          .eq('id', user.id)
          .maybeSingle();

        const isMember = profile?.subscription_tier === 'team_member';
        setIsTeamMember(isMember);

        if (isMember) {
          const { data: teamRow } = await supabase
            .from('team_members')
            .select('team_owner_id')
            .eq('member_user_id', user.id)
            .eq('is_active', true)
            .maybeSingle();

          if (teamRow?.team_owner_id) {
            subscribeToStatus(teamRow.team_owner_id);
          } else {
            setPaymentFailed(false);
          }
          return;
        }

        subscribeToStatus(user.id);
      } catch (error) {
        console.error('PaymentGuard init error:', error);
      }
    };

    init();

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [loading]);

  // Query DB for latest subscription status — called on initial connect and on reconnect
  const recheckStatus = async (userId) => {
    try {
      const { data: latestSub } = await supabase
        .from('user_subscriptions')
        .select('status')
        .eq('user_id', userId)
        .order('start_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      const failed = latestSub?.status && latestSub.status !== 'active' && latestSub.status !== 'cancelled';
      setPaymentFailed(!!failed);
    } catch (error) {
      console.error('PaymentGuard recheck error:', error);
    }
  };

  // Subscribe to user_subscriptions realtime for a given userId
  const subscribeToStatus = (userId) => {
    if (watchedUserIdRef.current === userId) return;
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    watchedUserIdRef.current = userId;
    const channel = supabase
      .channel(`payment-guard-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const status = payload.new?.status;
          if (!status) return;
          if (status === 'active' || status === 'cancelled') {
            setPaymentFailed(false);
          } else {
            setPaymentFailed(true);
          }
        }
      )
      .subscribe((channelStatus) => {
        if (channelStatus === 'SUBSCRIBED') {
          recheckStatus(userId);
        }
      });

    realtimeChannelRef.current = channel;
  };

  const handleCheckPayment = async () => {
    setChecking(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-payment-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      const result = await response.json();
      console.log('check-payment-status response:', result);

      if (result.paid) {
        setPaymentFailed(false);
        await refreshProfile();
      } else {
        alert(result.message || 'Payment is still pending');
      }
    } catch (err) {
      console.error('Check error:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleCancelConfirmed = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setPaymentFailed(false);
        await refreshProfile();
        navigate('/main-dashboard');
      }
    } catch (err) {
      console.error('Cancel error:', err);
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
      setCancelStep(1);
    }
  };

  return (
    <>
      {children}

      {paymentFailed && !dismissed && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 px-3 sm:px-4 py-3"
          style={{
            background: 'rgba(20, 20, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          {/* Dismiss button — top right on mobile */}
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 sm:hidden p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all duration-200"
            title="Dismiss"
          >
            <Icon name="X" size={16} />
          </button>

          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Message */}
            <div className="flex items-center gap-2 flex-1 min-w-0 pr-8 sm:pr-0">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(239, 68, 68, 0.15)' }}
              >
                <Icon name="AlertCircle" size={18} className="text-red-400" />
              </div>
              <div>
                <p className="text-white/70 text-sm">
                  <span className="text-red-400 font-medium">Payment failed</span>
                </p>
                <p className="text-white/40 text-xs mt-0.5">
                  {isTeamMember
                    ? 'Please contact your Team Owner to resolve the payment issue.'
                    : 'Please update your payment method. Stripe will auto-retry.'}
                </p>
              </div>
            </div>

            {/* Buttons — hide action buttons for team members */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
              {!isTeamMember && (
                <>
                  <button
                    onClick={handleCheckPayment}
                    disabled={checking}
                    className="px-4 py-2.5 sm:py-2 rounded-xl text-xs font-medium text-black transition-all duration-200 hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, #F46118, #FE8338)',
                    }}
                  >
                    {checking ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Icon name="Loader2" size={14} className="animate-spin" />
                        Checking...
                      </span>
                    ) : (
                      'Check Payment Status'
                    )}
                  </button>

                  <button
                    onClick={() => { setCancelStep(1); setShowCancelModal(true); }}
                    disabled={cancelling}
                    className="px-4 py-2.5 sm:py-2 rounded-xl text-xs font-medium text-white/60 transition-all duration-200 hover:text-white hover:bg-white/10"
                    style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {cancelling ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Icon name="Loader2" size={14} className="animate-spin" />
                        Cancelling...
                      </span>
                    ) : (
                      'Cancel & Use Free'
                    )}
                  </button>
                </>
              )}

              {/* Dismiss button — inline on desktop */}
              <button
                onClick={() => setDismissed(true)}
                className="hidden sm:flex p-2 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all duration-200"
                title="Dismiss"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" style={{ backdropFilter: 'blur(8px)' }}>
          <div
            className="max-w-md w-full rounded-2xl p-6"
            style={{
              background: 'rgba(20, 20, 20, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(254, 131, 56, 0.15)',
            }}
          >
            {cancelStep === 1 && (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(254, 131, 56, 0.1)', border: '1px solid rgba(254, 131, 56, 0.2)' }}
                  >
                    <Icon name="AlertTriangle" size={22} className="text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Cancel Subscription?</h3>
                    <p className="text-xs text-white/40">Are you sure you want to cancel your subscription?</p>
                  </div>
                </div>

                <div
                  className="p-4 rounded-xl mb-5"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <p className="text-xs text-white/50 mb-2">If you cancel, you will:</p>
                  <ul className="text-xs text-white/40 space-y-1.5">
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Lose access to premium features</li>
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Be downgraded to the Free plan</li>
                    <li className="flex items-center gap-2"><Icon name="X" size={14} className="text-orange-400" />Team members will be removed permanently</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelStep(2)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-red-400 border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="XCircle" size={16} />
                    Yes, Cancel
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-all"
                  >
                    Keep Plan
                  </button>
                </div>
              </>
            )}

            {cancelStep === 2 && (
              <>
                <div className="flex items-start gap-3 mb-5">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                  >
                    <Icon name="AlertOctagon" size={22} className="text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">This cannot be undone</h3>
                    <p className="text-xs text-white/40">Your plan will be downgraded to Free immediately. You will lose your remaining credits and premium access.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleCancelConfirmed}
                    disabled={cancelling}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Icon name="XCircle" size={16} />
                    {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                  </button>
                  <button
                    onClick={() => { setShowCancelModal(false); setCancelStep(1); }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-all"
                  >
                    Go Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentGuard;
